/**
 * TanStack Query hook for `GET /api/cro/metrics/trend/:metricId`.
 *
 * Used by the 2 trend tiles (CRO-06 AE Meetings/Week, CRO-07 Active Opps/AE).
 * Returns CroTrendResponse which includes the 8-week sparkline array.
 *
 * Config:
 *   - queryKey: ['cro', 'trend', metricId]
 *   - staleTime: 60s — cache-first from agg_cro_scorecard_trend (10-min cron)
 *   - retry: 2 — fetch-level retries on blips
 */
import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import {
  apiFetch,
  type ApiClientError,
  type ApiServerError,
  type CacheNotReadyError,
  type SalesforceUnavailableError,
  type UnauthenticatedError,
} from '../lib/api-client.js';

import type { CroTrendResponse } from '@saledash/shared';

export type MetricError =
  | SalesforceUnavailableError
  | CacheNotReadyError
  | ApiServerError
  | ApiClientError
  | UnauthenticatedError;

export function useCroTrend(
  metricId: 'CRO-06' | 'CRO-07',
): UseQueryResult<CroTrendResponse, MetricError> {
  return useQuery<CroTrendResponse, MetricError>({
    queryKey: ['cro', 'trend', metricId],
    queryFn: ({ signal }) =>
      apiFetch<CroTrendResponse>(`/api/cro/metrics/trend/${metricId}`, { signal }),
    staleTime: 60_000,
    retry: 2,
  });
}
